/**
 * @fileoverview Опасные ситуации (закрытая полоса / сближение / занос).
 */

import { Snd } from "../audio/sound";
import type {
  GameHooks,
  GameState,
  Hazard,
  HazardKind,
  Plane,
  Runway,
} from "../state/types";
import { dist } from "../util/math";
import { taxiCollision } from "./collisions";
import { violation } from "./scoring";

/** Запустить hazard, если такого еще нет. */
export function startHazard(
  g: GameState,
  hooks: GameHooks,
  kind: HazardKind,
  p: Plane | null,
  r: Runway | null,
  t: number,
): void {
  if (
    g.hazards.some(
      (h) => h.kind === kind && (h.p === p || (!p && h.r === r)) && h.r === r,
    )
  ) {
    return;
  }
  g.hazards.push({ kind, p, r, t, max: t });
  Snd.warn();
  g.flash = 0.7;
  if (kind === "closed" && p) {
    hooks.toast(`ОПАСНО: ${p.cs} заходит на закрытую полосу!`, "bad");
  } else if (kind === "near") {
    hooks.toast("ОПАСНОЕ СБЛИЖЕНИЕ на рулежке!", "bad");
  }
}

/** Убрать hazards, связанные с бортом. */
export function killHazardsFor(g: GameState, p: Plane): void {
  g.hazards = g.hazards.filter((h) => h.p !== p && h.q !== p);
}

/** Тик таймеров hazards. */
export function updateHazards(
  g: GameState,
  hooks: GameHooks,
  dt: number,
): void {
  for (let i = g.hazards.length - 1; i >= 0; i--) {
    const h = g.hazards[i]!;
    h.t -= dt;
    if (h.kind === "closed") {
      updateClosedHazard(g, hooks, h, i);
    } else if (h.kind === "near") {
      updateNearHazard(g, hooks, h, i);
    } else if (h.kind === "snow") {
      updateSnowHazard(g, hooks, h, i);
    }
  }
}

function updateClosedHazard(
  g: GameState,
  hooks: GameHooks,
  h: Hazard,
  i: number,
): void {
  const p = h.p;
  const r = h.r;
  const active =
    p &&
    (p.status === "inbound" ||
      p.status === "landing" ||
      p.status === "takeoff");
  if (!r || !p || !r.closed || p.dead || !active) {
    g.hazards.splice(i, 1);
    return;
  }
  if (h.t <= 1.6 && r.clearSnowT > 0) {
    r.clearSnowT = 0;
    r.clearSnowMax = 0;
    if (!(r.tempCloseT > 0)) r.closed = false;
    hooks.toast(`${r.name}: уборка прервана из‑за конфликта`, "bad");
  }
  if (h.t <= 0) {
    g.hazards.splice(i, 1);
    violation(g, hooks, `посадка на закрытую полосу (${p.cs})`);
  }
}

function updateNearHazard(
  g: GameState,
  hooks: GameHooks,
  h: Hazard,
  i: number,
): void {
  const a = h.p;
  const b = h.q;
  if (!a || !b || a.dead || b.dead) {
    g.hazards.splice(i, 1);
    return;
  }
  const d = dist(a.x, a.y, b.x, b.y);
  if (d > 90) {
    g.hazards.splice(i, 1);
    return;
  }
  if (d < 30) {
    g.hazards.splice(i, 1);
    taxiCollision(g, hooks, a, b);
    return;
  }
  if (h.t <= 0) {
    g.hazards.splice(i, 1);
    if (d < 32) taxiCollision(g, hooks, a, b);
  }
}

function updateSnowHazard(
  g: GameState,
  hooks: GameHooks,
  h: Hazard,
  i: number,
): void {
  const r = h.r;
  if (!r || r.snow < 70 || r.closed) {
    g.hazards.splice(i, 1);
    if (r) r.critArmed = true;
    return;
  }
  if (h.t <= 0) {
    g.hazards.splice(i, 1);
    r.snowCool = 48;
    r.critArmed = false;
    violation(g, hooks, `критический занос ${r.name}`);
  }
}
